"use client";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  useMediaQuery,
  Badge,
  useTheme,
  alpha,
} from "@mui/material";
import {
  PersonTwoTone as PersonIcon,
  SearchTwoTone as SearchIcon,
  FavoriteTwoTone as FavoriteIcon,
  SettingsTwoTone as SettingsIcon,
  LogoutTwoTone as LogoutIcon,
  MenuTwoTone as MenuIcon,
  CardMembershipTwoTone as CardMembershipIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { useWishlist } from "../../contexts/WishlistContext";
import { useToast } from "../../contexts/ToastContext";

import apiEndpoints from "../../apiConfig";
import LogoutConfirmDialog from "../ui/LogoutConfirmDialog";

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { wishlist } = useWishlist();
  const { showToast } = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [enquiryCount, setEnquiryCount] = useState(0); // Enquiry Count state
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  useEffect(() => {
    if (!isMobile) {
      setMobileOpen(false);
    }
  }, [isMobile]);

  // Fetch Enquiry Count
  useEffect(() => {
    if (!user?.id) return;

    const fetchCount = () => {
      fetch(`${apiEndpoints.getEnquiryCount}?user_id=${user.id}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setEnquiryCount(data.count);
          }
        })
        .catch((err) => console.error("Error fetching enquiry count:", err));
    };

    const handleCountEvent = (event) => {
      const nextCount = event?.detail?.count;
      if (typeof nextCount === "number") {
        setEnquiryCount(nextCount);
      }
    };

    fetchCount();
    window.addEventListener("enquiryCountChanged", handleCountEvent);

    return () => {
      window.removeEventListener("enquiryCountChanged", handleCountEvent);
    };
  }, [user, location.pathname]);

  if (!user) return null;

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    logout();
    showToast("Logged out successfully", "success");
    navigate("/");
  };

  const menuItems = [
    { label: "Profiles", path: "/home", icon: <PersonIcon /> },
    { label: "Search", path: "/search", icon: <SearchIcon /> },
    {
      label: "Wishlist",
      path: "/wishlist",
      icon: (
        <Badge badgeContent={wishlist.length || null} color="error">
          <FavoriteIcon />
        </Badge>
      ),
    },
    {
      label: "Membership",
      path: "/membership",
      icon: <CardMembershipIcon />,
    },
    {
      label: "Enquiries",
      path: "/requested-enquiries",
      icon: (
        <Badge badgeContent={enquiryCount || null} color="error">
          <PersonIcon />
        </Badge>
      ),
    },
    { label: "Settings", path: "/settings", icon: <SettingsIcon /> },
  ];

  const drawer = (
    <Box
      sx={{ width: 230, marginTop: 7 }}
      role="presentation"
      onClick={() => setMobileOpen(false)}
    >
      <List>
        {menuItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButton onClick={() => navigate(item.path)}>
              {item.icon}
              <ListItemText primary={item.label} sx={{ marginLeft: 1 }} />
            </ListItemButton>
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogoutClick}>
            <LogoutIcon />
            <ListItemText primary="Logout" sx={{ marginLeft: 1 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: alpha(theme.palette.background.paper, 0.95), // Dynamic background
        backdropFilter: "blur(12px)", // Glassmorphism
        color: "text.primary",
        boxShadow: "none",
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 64, md: 80 },
          px: { xs: 1, sm: 2, md: 4 },
          py: { xs: 0.5, md: 1 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Logo */}
        <Box
          component="img"
          src="/Logokalyani.png"
          alt="Kalyani Matrimony"
          sx={{
            height: { xs: 40, sm: 50, md: 60, lg: 70 },
            width: "auto",
            objectFit: "contain",
            cursor: "pointer",
            flexShrink: 0,
          }}
          onClick={() => navigate("/home")}
        />

        {/* Desktop Menu */}
        {!isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { md: 1, lg: 2, xl: 3 },
              ml: "auto",
            }}
          >
            {menuItems.map((item, index) => (
              <Button
                key={index}
                color="inherit"
                startIcon={item.icon}
                onClick={() => navigate(item.path)}
                sx={{
                  fontWeight:
                    location.pathname === item.path ? "bold" : "normal",
                  fontSize: { md: "0.75rem", lg: "0.875rem", xl: "1rem" },
                  minWidth: "auto",
                  px: { md: 1, lg: 1.5, xl: 2 },
                  "& .MuiButton-startIcon": {
                    marginRight: { md: 0.5, lg: 1 },
                  },
                }}
              >
                {isTablet ? "" : item.label}
              </Button>
            ))}
            <Typography
              variant="body2"
              sx={{
                color: "black",
                display: { md: "none", lg: "block" },
                fontSize: { lg: "0.875rem", xl: "1rem" },
                ml: 1,
              }}
            >
              Welcome, {user.name || "User"}
            </Typography>
            <Button
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogoutClick}
              sx={{
                fontSize: { md: "0.75rem", lg: "0.875rem", xl: "1rem" },
                minWidth: "auto",
                px: { md: 1, lg: 1.5, xl: 2 },
                "& .MuiButton-startIcon": {
                  marginRight: { md: 0.5, lg: 1 },
                },
              }}
            >
              {isTablet ? "" : "Logout"}
            </Button>
          </Box>
        )}

        {/* Mobile Menu */}
        {isMobile && (
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setMobileOpen(true)}
            sx={{ ml: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Toolbar>

      {/* Drawer for Mobile */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
      >
        {drawer}
      </Drawer>

      <LogoutConfirmDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleConfirmLogout}
      />
    </AppBar>
  );
}

export default Navbar;
